export type SelfCheckOption = {
  key: string;
  label: string;
};

export type SelfCheckSection = {
  id: string;
  title: string;
  subtitle?: string;
  options: SelfCheckOption[];
  tip: string;
};

function toOptions(labels: string[]): SelfCheckOption[] {
  return labels.map((label, index) => ({ key: `${index}`, label }));
}

export const SELF_CHECK_SECTIONS: SelfCheckSection[] = [
  {
    id: "reason",
    title: "您为什么开始考虑海外身份？",
    options: toOptions([
      "为子女未来海外教育提前布局",
      "希望获得海外永居或者护照",
      "为全球商务和出行提供便利",
      "希望未来在海外生活或退休",
      "企业正在进行国际化发展",
      "已经开始配置海外资产",
      "希望降低家庭资产过度集中在单一国家的风险",
      "希望为下一代财富传承提前安排",
    ]),
    tip: "如果你的目标同时涉及身份、教育、资产和下一代，那么需要考虑的通常已经不是单一投资移民项目，而是一套家庭长期规划。",
  },
  {
    id: "familyLocation",
    title: "未来10年，你的家庭成员会生活在哪里？",
    options: toOptions([
      "全家仍以国内生活为主",
      "子女未来可能在海外发展",
      "尚未确定，需要保留更多选择",
    ]),
    tip: "你需要考虑的不只是“谁拿身份”，更重要的是家庭成员未来分别在哪个国家生活、工作、读书和持有资产。这会直接影响：身份规划、税务居民身份、房产持有、资产传承以及未来家庭财富结构。",
  },
  {
    id: "assetLocation",
    title: "你的家庭资产目前分布在哪里？",
    options: toOptions([
      "主要资产都在境内",
      "家庭资产已经分布在两个或以上国家",
      "正准备进行更多海外资产配置",
    ]),
    tip: "你的身份在哪里，资产在哪里，税务关系在哪里？如果这三个问题没有一起规划，未来很容易出现：资产结构不合理、税务成本增加、跨境继承复杂、家庭成员之间安排冲突等问题。",
  },
  {
    id: "assetScale",
    title: "你的家庭可投资资产规模是多少？",
    options: toOptions(["100万—500万元", "500万—1000万元", "1000万—3000万元", "3000万以上"]),
    tip: "不是这笔投资多少钱，而是这笔投资应该占家庭总资产多少比例？任何投资都不应该成为孤立决策。",
  },
  {
    id: "wealthSource",
    title: "你的财富主要来自哪里？",
    options: toOptions([
      "工资收入",
      "企业经营",
      "房产投资",
      "股票、基金等金融投资",
      "家族财富继承或赠与",
    ]),
    tip: "不同来源的财富，在税务申报、资产架构与传承安排上的最优路径也不同，这也是我们做整体方案时的重要参考。",
  },
  {
    id: "holdingStructure",
    title: "你准备如何持有海外资产？",
    subtitle: "假设未来准备购买海外房产或投资，你会选择：",
    options: toOptions([
      "个人名义",
      "夫妻共同持有",
      "SPV公司持有",
      "信托持有",
      "家族投资公司持有",
      "暂时不知道",
    ]),
    tip: "不同持有结构，未来可能影响：税务、融资、出售、继承、隐私保护以及家族财富传承。家族办公室的核心之一，不是单纯帮助客户“买资产”，而是提前考虑：谁来持有、如何持有、未来如何退出和传承。",
  },
  {
    id: "taxIssues",
    title: "你是否考虑过身份变化带来的税务问题？",
    options: toOptions([
      "目前没有考虑",
      "不清楚什么是税务居民",
      "担心海外收入需要申报",
      "名下有多个国家的房产",
      "持有境内外公司股权",
      "有大额金融投资",
      "已经涉及英国、美国等国家税务问题",
      "未来可能成为海外税务居民",
      "担心遗产税或继承问题",
      "已经在考虑家族信托",
    ]),
    tip: "很多家庭的顺序是：先移民 → 再投资或置业 → 最后发现税务问题。更合理的顺序应该是：先看家庭整体结构 → 税务统筹规划方案 → 再决定身份 → 再决定资产如何配置。",
  },
  {
    id: "childrenExpectation",
    title: "你对子女未来最大的期待是什么？",
    options: toOptions([
      "接受海外顶级教育资源",
      "未来留在海外工作",
      "有更多国家可以自由选择",
      "未来接班家族企业",
      "希望子女独立发展",
      "希望逐步把部分家庭财富传给下一代",
    ]),
    tip: "对于很多家庭而言，投资移民真正投资的，并不是一张身份卡，而是下一代未来的选择权。",
  },
  {
    id: "wealthSuccession",
    title: "你的家庭是否已经进入财富传承阶段？",
    options: toOptions([
      "暂时没有考虑",
      "开始考虑资产如何留给下一代",
      "担心未来遗产税",
      "考虑家族信托",
      "希望建立长期全球家族财富管理机制",
    ]),
    tip: "如果符合多项，那么你的核心问题已经从财富积累，变成家庭财富如何跨代际长期安排。",
  },
  {
    id: "fragmentedManagement",
    title: "你的家庭是否存在以下“分散管理”问题？",
    options: toOptions([
      "移民找一家机构",
      "买房找一家机构",
      "税务找一家机构",
      "海外投资找一家机构",
      "公司架构找一家机构",
      "信托找一家机构",
      "各个机构之间彼此不了解家庭完整情况",
      "家庭自己承担协调所有专业机构的工作",
    ]),
    tip: "如果这一部分你勾选很多，这往往就是家族办公室真正能够解决的问题：把原本分散在不同机构、不同国家、不同专业领域的问题，放到同一个家庭资产框架下统筹解决。",
  },
];

export const SELF_CHECK_RESULT_THRESHOLD = 5;
